import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { Plus, CircleNotch, Sparkle, Robot, Clock, CaretDown } from "@phosphor-icons/react";
import TemplateRow from "@/components/TemplateRow";

const CATEGORIES = ["ventas", "servicio", "repuestos", "financiamiento", "general", "post_venta", "saludo", "cierre"];

// Human-readable labels per category (both languages) + phosphor icon for the group header.
const CATEGORY_META = {
  ventas:        { es: "Ventas",           en: "Sales",        color: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30" },
  servicio:      { es: "Servicio",         en: "Service",      color: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30" },
  repuestos:     { es: "Repuestos",        en: "Parts",        color: "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30" },
  financiamiento:{ es: "Financiamiento",   en: "Financing",    color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  general:       { es: "General",          en: "General",      color: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30" },
  post_venta:    { es: "Post-venta",       en: "After-sales",  color: "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30" },
  saludo:        { es: "Saludos",          en: "Greetings",    color: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  cierre:        { es: "Cierre",           en: "Closing",      color: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30" },
};

export default function MessageTemplatesEditor({ lang }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [draft, setDraft] = useState({ title: "", content: "", content_en: "", category: "general", enabled: true });
  const [filter, setFilter] = useState({ q: "", category: "", source: "" });
  const [bdcCfg, setBdcCfg] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/message-templates?enabled_only=false");
      setItems(data || []);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    Promise.all([load(), api.get("/bdc/config").then((r) => setBdcCfg(r.data)).catch(() => {})])
      .finally(() => setLoading(false));
  }, []);

  async function create() {
    if (!draft.title.trim() || !draft.content.trim()) {
      toast.error(lang === "es" ? "Título y contenido requeridos" : "Title and content required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/message-templates", draft);
      setItems((c) => [...c, data]);
      setDraft({ title: "", content: "", content_en: "", category: "general", enabled: true });
      toast.success(lang === "es" ? "Plantilla agregada" : "Template added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function patch(id, upd) {
    try {
      const { data } = await api.patch(`/message-templates/${id}`, upd);
      setItems((c) => c.map((t) => (t.id === id ? data : t)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  }

  async function save(id, upd) {
    try {
      const { data } = await api.patch(`/message-templates/${id}`, upd);
      setItems((c) => c.map((t) => (t.id === id ? data : t)));
      toast.success(lang === "es" ? "Plantilla actualizada" : "Template updated");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      return false;
    }
  }

  async function remove(id) {
    if (!window.confirm(lang === "es" ? "¿Eliminar esta plantilla?" : "Delete this template?")) return;
    try {
      await api.delete(`/message-templates/${id}`);
      setItems((c) => c.filter((t) => t.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  }

  async function seedBDC(force = false) {
    if (force && !window.confirm(lang === "es"
      ? "¿Sobrescribir las 27 plantillas BDC con los valores originales?"
      : "Overwrite the 27 BDC templates with the original values?")) return;
    setSeeding(true);
    try {
      const { data } = await api.post(`/message-templates/seed-bdc?force=${force}`);
      const msg = lang === "es"
        ? `Seed BDC · +${data.inserted} nuevas · ${data.updated} actualizadas · ${data.skipped} omitidas`
        : `BDC seed · +${data.inserted} new · ${data.updated} updated · ${data.skipped} skipped`;
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSeeding(false);
    }
  }

  async function updateBdcCfg(patch) {
    try {
      const { data } = await api.patch("/bdc/config", patch);
      setBdcCfg(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  }

  const filtered = items.filter((t) => {
    if (filter.q && !((t.title + " " + t.content + " " + (t.code || "")).toLowerCase().includes(filter.q.toLowerCase()))) return false;
    if (filter.category && t.category !== filter.category) return false;
    if (filter.source === "bdc" && t.source !== "bdc_seed") return false;
    if (filter.source === "custom" && t.source === "bdc_seed") return false;
    return true;
  });

  const bdcCount = items.filter((t) => t.source === "bdc_seed").length;

  if (loading) return <div className="text-xs text-muted-foreground flex items-center gap-1.5"><CircleNotch size={12} className="animate-spin"/>Cargando…</div>;

  return (
    <div data-testid="message-templates-editor">
      <div className="text-sm font-medium mb-1">
        {lang === "es" ? "Plantillas de mensajes rápidos (BDC + custom)" : "Quick-reply templates (BDC + custom)"}
      </div>
      <div className="text-xs text-muted-foreground mb-3">
        {lang === "es"
          ? "Los agentes las insertan con un click desde el composer. La IA las usa como referencia de tono. Placeholders: {customer_name}, {agent_name}, {branch}, {date}, {time}, {advisor_name}."
          : "Agents insert them from the composer. The AI uses them as tone reference. Placeholders: {customer_name}, {agent_name}, {branch}, {date}, {time}, {advisor_name}."}
      </div>

      {/* BDC config panel */}
      {bdcCfg && (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 mb-3 flex flex-wrap items-center gap-3"
          data-testid="bdc-config-panel"
        >
          <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
            <Sparkle size={14} weight="fill" /> {lang === "es" ? "BDC Config" : "BDC Config"}
          </div>
          <label className="flex items-center gap-1.5 text-xs">
            <Robot size={12} />
            <Switch
              checked={bdcCfg.bot_reference_enabled}
              onCheckedChange={(v) => updateBdcCfg({ bot_reference_enabled: v })}
              data-testid="bdc-toggle-bot-ref"
            />
            {lang === "es" ? "IA usa referencia BDC" : "AI uses BDC reference"}
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <Clock size={12} />
            <Switch
              checked={bdcCfg.auto_followup_enabled}
              onCheckedChange={(v) => updateBdcCfg({ auto_followup_enabled: v })}
              data-testid="bdc-toggle-autofollow"
            />
            {lang === "es" ? "Auto follow-up al" : "Auto follow-up after"}
            <input
              type="number"
              value={bdcCfg.auto_followup_seconds}
              onChange={(e) => updateBdcCfg({ auto_followup_seconds: Number(e.target.value) })}
              className="w-14 h-6 rounded border border-input bg-background px-1.5 text-xs"
              min={10}
              max={300}
              disabled={!bdcCfg.auto_followup_enabled}
              data-testid="bdc-autofollow-seconds"
            />
            {lang === "es" ? "seg de espera" : "sec of wait"}
          </label>
          <div className="flex-1" />
          <div className="text-[10px] text-muted-foreground">
            {bdcCount} {lang === "es" ? "plantillas BDC seeded" : "BDC templates seeded"}
          </div>
        </div>
      )}

      {/* Seed + filter row */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => seedBDC(false)}
          disabled={seeding}
          data-testid="seed-bdc-btn"
        >
          {seeding ? <CircleNotch size={12} className="animate-spin mr-1"/> : <Sparkle size={12} className="mr-1"/>}
          {lang === "es" ? "Cargar Plantillas" : "Load Templates"}
        </Button>
        <div className="flex-1" />
        <Input
          placeholder={lang === "es" ? "Buscar…" : "Search…"}
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
          className="h-8 w-40 text-xs"
          data-testid="templates-search"
        />
        <select
          value={filter.category}
          onChange={(e) => setFilter({ ...filter, category: e.target.value })}
          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
          data-testid="templates-filter-category"
        >
          <option value="">{lang === "es" ? "Todas las categorías" : "All categories"}</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={filter.source}
          onChange={(e) => setFilter({ ...filter, source: e.target.value })}
          className="h-8 rounded-md border border-input bg-background px-2 text-xs"
          data-testid="templates-filter-source"
        >
          <option value="">{lang === "es" ? "Todas las fuentes" : "All sources"}</option>
          <option value="bdc">BDC ({bdcCount})</option>
          <option value="custom">{lang === "es" ? "Custom" : "Custom"}</option>
        </select>
      </div>

      {/* Add form (collapsible) */}
      <Collapsible open={addOpen} onOpenChange={setAddOpen} className="rounded-md border border-primary/30 bg-primary/5 mb-4">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            data-testid="template-add-toggle"
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium"
          >
            <span className="flex items-center gap-1.5 text-primary">
              <Plus size={12} weight="bold" />
              {lang === "es" ? "Nueva plantilla" : "New template"}
            </span>
            <CaretDown size={12} className={`transition-transform ${addOpen ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="px-3 pb-3">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            <Input
              placeholder={lang === "es" ? "Título (ej: Cita confirmada)" : "Title"}
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              data-testid="template-new-title"
              className="h-8 md:col-span-3"
            />
            <select
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              data-testid="template-new-category"
              className="h-8 rounded-md border border-input bg-background px-2 text-xs md:col-span-2"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Button size="sm" onClick={create} disabled={saving} data-testid="template-save" className="h-8">
              {saving ? <CircleNotch size={12} className="animate-spin"/> : <Plus size={12}/>}
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-2">
            <Textarea
              placeholder={lang === "es" ? "Contenido ES — usa {customer_name}, {agent_name}, {branch}…" : "Content ES — use {customer_name}, {agent_name}, {branch}…"}
              value={draft.content}
              onChange={(e) => setDraft({ ...draft, content: e.target.value })}
              data-testid="template-new-content"
              rows={3}
              className="text-xs"
            />
            <Textarea
              placeholder={lang === "es" ? "Contenido EN (opcional)" : "Content EN (optional)"}
              value={draft.content_en}
              onChange={(e) => setDraft({ ...draft, content_en: e.target.value })}
              data-testid="template-new-content-en"
              rows={3}
              className="text-xs"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* List — grouped by category */}
      {filtered.length === 0 ? (
        <div className="text-center text-xs text-muted-foreground py-6 border border-dashed border-border rounded-md">
          {lang === "es" ? "Sin plantillas que coincidan." : "No matching templates."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="templates-list">
          {CATEGORIES.map((cat) => {
            const inCat = filtered.filter((t) => (t.category || "general") === cat);
            if (inCat.length === 0) return null;
            const meta = CATEGORY_META[cat] || { es: cat, en: cat, color: "bg-muted text-foreground border-border" };
            return (
              <details
                key={cat}
                open
                className="rounded-md border border-border bg-card/40 overflow-hidden group"
                data-testid={`template-cat-${cat}`}
              >
                <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between gap-2 hover:bg-muted/40">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] uppercase font-semibold tracking-wider ${meta.color}`}>
                      {lang === "es" ? meta.es : meta.en}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {inCat.length} {lang === "es" ? "plantillas" : "templates"}
                    </span>
                  </div>
                  <CaretDown size={12} className="transition-transform group-open:rotate-180 text-muted-foreground" />
                </summary>
                <div className="px-3 pb-3 pt-1 space-y-2">
                  {inCat.map((t) => (
                    <TemplateRow key={t.id} t={t} lang={lang} categories={CATEGORIES}
                      onSave={save} onToggle={(id, v) => patch(id, { enabled: v })} onDelete={remove} />
                  ))}
                </div>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
}
